import type { ReactNode } from "react";
import { Box, Button } from "@mui/material";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { ChatHandleGateDialog } from "Components/Chat/ChatHandleGateDialog";
import { ChatLoadingSkeleton } from "Components/Chat/ChatLoadingSkeleton";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { PhoneVerificationDialog } from "Components/Shared/PhoneVerification/PhoneVerificationDialog";
import { useIsMobile } from "Hooks/useIsMobile";
import { usePhoneVerificationStatus } from "Hooks/usePhoneVerificationStatus";
import { useNavigate } from "Navigation/useNavigate";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";

type Props = {
  children: ReactNode;
};

/**
 * Chat onboarding gate: everything chat needs from a persona is collected right here, over a
 * blurred chat skeleton, the first time they open chat — first a handle (others find and message
 * them by it), then a verified phone number. Both steps read server state, so saving a handle or
 * verifying the phone advances the gate by itself, and a persona who already has both never sees
 * it. The only way out without finishing is back to the dashboard.
 */
export const ChatOnboardingGate = ({ children }: Props) => {
  const { t } = useResource();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // React Query user-info, not the Redux user — the SPA sign-in result carries no handle.
  const { data: userInfo, isLoading: isUserInfoLoading } = useUserInfoQuery();
  const phone = usePhoneVerificationStatus();

  if (isUserInfoLoading || phone.isLoading) {
    return <CenteredCircularProgress />;
  }

  // Asserted only once user-info has loaded (as in useHandleGate): a failed user-info request
  // must not lock someone out of chat over a handle they may well have.
  const needsHandle = !!userInfo && !userInfo.handle;
  const needsPhone = !phone.isVerified;

  if (!needsHandle && !needsPhone) {
    return <>{children}</>;
  }

  const backToDashboard = (
    <Button onClick={() => navigate(appUrl("dashboard"))}>
      {t(Resources.Chat.BackToDashboard)}
    </Button>
  );

  return (
    <Box position="relative" height="100%" width="100%">
      <Box
        height="100%"
        width="100%"
        sx={{ filter: "blur(8px)", pointerEvents: "none", opacity: 0.4 }}
      >
        <ChatLoadingSkeleton isMobile={isMobile} isChatDetailView={false} />
      </Box>
      {needsHandle ? (
        <ChatHandleGateDialog isOpen secondaryAction={backToDashboard} />
      ) : (
        <PhoneVerificationDialog
          isOpen
          defaultPhone={phone.phone ?? undefined}
          hasPhone={phone.hasPhone}
          secondaryAction={backToDashboard}
        />
      )}
    </Box>
  );
};
